import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, ClickAwayListener, Divider, IconButton, Popper, useTheme } from '@mui/material';
import { ListStyleType } from '@platejs/list';
import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';
import {
  Bold,
  ChevronRight,
  EllipsisVertical,
  Italic,
  Link,
  List,
  ListOrdered,
  ListTodo,
  Strikethrough,
  Table,
  Underline,
} from 'lucide-react';
import { useEditorRef, useMarkToolbarButton, useMarkToolbarButtonState } from 'platejs/react';
import { AlineTooltip } from 'src/components';
import { useResponsiveOverflow } from 'src/hooks/dom-hooks';
import { makeStyles } from 'tss-react/mui';

import { LinkToolbarButton } from './plugins/LinkKit';
import { ListToolbarButton } from './plugins/ListKit';
import { TableToolbarButton } from './plugins/TableKit';
import { focusEditor } from './utils/focus';
import { getFormatToolbarStyles, getToolbarButtonStyles } from './utils/styles';

type ToolbarItemDef =
  | { Icon: LucideIcon; id: string; nodeType: string; tooltip: string; type: 'mark' }
  | {
      Icon: LucideIcon;
      id: string;
      listStyleType: ListStyleType | string;
      tooltip: string;
      type: 'list';
    }
  | { Icon: LucideIcon; id: string; tooltip: string; type: 'link' }
  | { Icon: LucideIcon; id: string; tooltip: string; type: 'table' }
  | { id: string; type: 'divider' };

const ICON_SIZE = 18;
// The keyCode browsers report for every key pressed while an IME is composing.
const IME_COMPOSING_KEY_CODE = 229;

/**
 * Toolbar items in priority order (first = most important, last = first to overflow).
 * This single array defines both the visible toolbar and overflow menu contents.
 */
const TOOLBAR_ITEMS: ToolbarItemDef[] = [
  {
    Icon: Bold,
    id: 'bold',
    nodeType: 'bold',
    tooltip: 'Bold (Cmd+B)',
    type: 'mark',
  },
  {
    Icon: Italic,
    id: 'italic',
    nodeType: 'italic',
    tooltip: 'Italic (Cmd+I)',
    type: 'mark',
  },
  {
    Icon: Underline,
    id: 'underline',
    nodeType: 'underline',
    tooltip: 'Underline (Cmd+U)',
    type: 'mark',
  },
  {
    Icon: Strikethrough,
    id: 'strikethrough',
    nodeType: 'strikethrough',
    tooltip: 'Strikethrough',
    type: 'mark',
  },
  { id: 'div-1', type: 'divider' },
  {
    Icon: ListOrdered,
    id: 'numbered',
    listStyleType: ListStyleType.Decimal,
    tooltip: 'Numbered list',
    type: 'list',
  },
  {
    Icon: List,
    id: 'bulleted',
    listStyleType: ListStyleType.Disc,
    tooltip: 'Bulleted list',
    type: 'list',
  },
  {
    Icon: ListTodo,
    id: 'todo',
    listStyleType: 'todo',
    tooltip: 'Todo list',
    type: 'list',
  },
  { id: 'div-2', type: 'divider' },
  {
    Icon: Link,
    id: 'link',
    tooltip: 'Insert link (Cmd+K)',
    type: 'link',
  },
  {
    Icon: Table,
    id: 'table',
    tooltip: 'Insert table',
    type: 'table',
  },
];

const EMPTY_PARAGRAPH_ITEM_IDS = ['numbered', 'bulleted', 'todo', 'table', 'link'];
const EMPTY_PARAGRAPH_ITEMS = TOOLBAR_ITEMS.filter(item =>
  EMPTY_PARAGRAPH_ITEM_IDS.includes(item.id)
);

const useToolbarStyles = makeStyles()(theme => {
  const sharedStyles = getToolbarButtonStyles(theme);
  return {
    divider: {
      height: 20,
      margin: theme.spacing(0, 0.25),
    },
    floatingToolbar: {
      alignItems: 'center',
      backgroundColor: theme.palette.common.white,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[4],
      display: 'flex',
      gap: theme.spacing(0.5),
      padding: theme.spacing(0.5),
    },
    formatToolbar: getFormatToolbarStyles(theme),
    measuringContainer: {
      // Hidden container to measure content width
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(0.25),
      opacity: 0,
      pointerEvents: 'none' as const,
      position: 'absolute' as const,
    },
    toolbar: {
      alignItems: 'center',
      backgroundColor: 'transparent',
      boxSizing: 'border-box',
      display: 'flex',
      gap: theme.spacing(0.25),
      padding: theme.spacing(0.5, 1),
      position: 'relative' as const,
      width: '100%',
    },
    toolbarButton: {
      ...sharedStyles.toolbarButton,
      '& svg': {
        display: 'block',
      },
      fontSize: '1rem',
    },
    toolbarButtonDefault: sharedStyles.toolbarButtonDefault,
    toolbarButtonPressed: sharedStyles.toolbarButtonPressed,
  };
});

/** Mark toolbar button for text formatting */
export const ToolbarButton = React.memo(
  React.forwardRef<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    {
      children: React.ReactNode;
      nodeType: string;
      tooltip: string;
      afterClick?: () => void;
      disabled?: boolean;
    }
  >(function ToolbarButton(
    { afterClick, children, disabled, nodeType, tooltip },
    ref
  ): JSX.Element {
    const theme = useTheme();
    const { classes } = useToolbarStyles();
    const editor = useEditorRef();
    const state = useMarkToolbarButtonState({ nodeType });
    const { props: buttonProps } = useMarkToolbarButton(state);

    const isPressed = !disabled && state.pressed;

    const handleClick = () => {
      buttonProps.onClick?.();
      afterClick?.();
      focusEditor(editor);
    };

    return (
      <AlineTooltip arrow placement="top" title={disabled ? '' : tooltip}>
        <IconButton
          ref={ref}
          aria-label={tooltip}
          aria-pressed={isPressed}
          className={clsx(
            classes.toolbarButton,
            isPressed ? classes.toolbarButtonPressed : classes.toolbarButtonDefault
          )}
          disabled={disabled}
          size="small"
          style={isPressed ? { backgroundColor: theme.palette.action.hover } : undefined}
          onClick={handleClick}
        >
          {children}
        </IconButton>
      </AlineTooltip>
    );
  })
);

/**
 * Renders toolbar item content based on type.
 * Separated from wrapper to keep measurement logic clean.
 */
function ToolbarItemContent({
  afterAction,
  disabled,
  item,
}: {
  item: ToolbarItemDef;
  afterAction?: () => void;
  disabled?: boolean;
}): JSX.Element {
  const { classes } = useToolbarStyles();

  switch (item.type) {
    case 'mark':
      return (
        <ToolbarButton
          afterClick={afterAction}
          disabled={disabled}
          nodeType={item.nodeType}
          tooltip={item.tooltip}
        >
          <item.Icon size={ICON_SIZE} />
        </ToolbarButton>
      );
    case 'list':
      return (
        <ListToolbarButton
          afterClick={afterAction}
          disabled={disabled}
          listStyleType={item.listStyleType}
          tooltip={item.tooltip}
        >
          <item.Icon size={ICON_SIZE} />
        </ListToolbarButton>
      );
    case 'link':
      return (
        <LinkToolbarButton afterClick={afterAction} disabled={disabled} tooltip={item.tooltip}>
          <item.Icon size={ICON_SIZE} />
        </LinkToolbarButton>
      );
    case 'table':
      return (
        <TableToolbarButton afterClose={afterAction} disabled={disabled} tooltip={item.tooltip}>
          <item.Icon size={ICON_SIZE} />
        </TableToolbarButton>
      );
    case 'divider':
      return <Divider flexItem className={classes.divider} orientation="vertical" />;
  }
}

/**
 * Renders a single toolbar item wrapped in a span for consistent measurement.
 * The wrapper span ensures all items have the same ref type (HTMLSpanElement),
 * avoiding type assertions when different buttons have different element types.
 */
const ToolbarItemRenderer = React.memo(function ToolbarItemRenderer({
  afterAction,
  disabled,
  forwardedRef,
  item,
}: {
  item: ToolbarItemDef;
  afterAction?: () => void;
  disabled?: boolean;
  forwardedRef?: React.Ref<HTMLSpanElement>;
}): JSX.Element {
  return (
    <span ref={forwardedRef} style={{ display: 'inline-flex' }}>
      <ToolbarItemContent afterAction={afterAction} disabled={disabled} item={item} />
    </span>
  );
});

/** Overflow menu for responsive toolbar - receives items to render dynamically */
export const ToolbarOverflow = React.memo(function ToolbarOverflow({
  disabled,
  items,
}: {
  items: ToolbarItemDef[];
  disabled?: boolean;
}): JSX.Element {
  const { classes } = useToolbarStyles();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleToggle = () => setOpen(prev => !prev);
  const handleClose = () => setOpen(false);

  // Filter out dividers - they don't add value in a compact overflow menu.
  const filteredItems = useMemo(() => items.filter(item => item.type !== 'divider'), [items]);

  return (
    <>
      <AlineTooltip arrow placement="top" title={open ? 'Collapse' : 'More options'}>
        <IconButton
          ref={buttonRef}
          className={clsx(
            classes.toolbarButton,
            open ? classes.toolbarButtonPressed : classes.toolbarButtonDefault
          )}
          disabled={disabled}
          size="small"
          style={open ? { backgroundColor: theme.palette.action.hover } : undefined}
          onClick={handleToggle}
        >
          <EllipsisVertical size={ICON_SIZE} />
        </IconButton>
      </AlineTooltip>

      <Popper
        anchorEl={buttonRef.current}
        className="ignore-click-outside/toolbar"
        open={open}
        placement="bottom-end"
        sx={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Box className={classes.floatingToolbar} sx={{ mt: 0.75 }}>
            {filteredItems.map(item => (
              <ToolbarItemRenderer
                key={item.id}
                afterAction={handleClose}
                disabled={disabled}
                item={item}
              />
            ))}
          </Box>
        </ClickAwayListener>
      </Popper>
    </>
  );
});

export interface AlineMarkdownEditorToolbarProps {
  disabled?: boolean;
  style?: React.CSSProperties;
}

/**
 * Toolbar for the AlineMarkdownEditor.
 * Uses a data-driven approach with automatic responsive overflow.
 * Items are defined once in TOOLBAR_ITEMS and automatically move to
 * overflow menu one-by-one as the container shrinks.
 */
export const AlineMarkdownEditorToolbar = React.memo(function AlineMarkdownEditorToolbar({
  disabled,
  style,
}: AlineMarkdownEditorToolbarProps): JSX.Element {
  const { classes } = useToolbarStyles();
  const { containerRef, getItemRef, overflowRef, visibleCount } =
    useResponsiveOverflow<HTMLDivElement>(TOOLBAR_ITEMS.length, 'horizontal');

  const visibleItems = TOOLBAR_ITEMS.slice(0, visibleCount);
  const overflowItems = TOOLBAR_ITEMS.slice(visibleCount);
  const showOverflow = overflowItems.length > 0;

  return (
    <Box ref={containerRef} className={classes.toolbar} style={style}>
      {/* Hidden measuring container - renders all items to get accurate measurements */}
      <div inert aria-hidden="true" className={classes.measuringContainer}>
        {TOOLBAR_ITEMS.map((item, index) => (
          <ToolbarItemRenderer key={item.id} forwardedRef={getItemRef(index)} item={item} />
        ))}
        {/* Measure overflow button too */}
        <div ref={overflowRef}>
          <IconButton className={classes.toolbarButton} size="small">
            <EllipsisVertical size={ICON_SIZE} />
          </IconButton>
        </div>
      </div>

      {/* Visible toolbar items */}
      {visibleItems.map(item => (
        <ToolbarItemRenderer key={item.id} disabled={disabled} item={item} />
      ))}

      {/* Overflow menu with remaining items */}
      {showOverflow && <ToolbarOverflow disabled={disabled} items={overflowItems} />}
    </Box>
  );
});

/** Quick insertion actions available directly on an empty paragraph. */
export const EmptyParagraphToolbar = React.memo(function EmptyParagraphToolbar() {
  const { classes } = useToolbarStyles();
  const toolbarRef = useRef<HTMLSpanElement>(null);
  // Where the caret stood when the full toolbar opened; null while it is closed.
  const [caretAnchor, setCaretAnchor] = useState<{ getBoundingClientRect: () => DOMRect } | null>(
    null
  );
  const open = Boolean(caretAnchor);
  const close = useCallback(() => setCaretAnchor(null), []);
  const openAtCaret = useCallback(() => {
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;
    const caretRect = selection.getRangeAt(0).getBoundingClientRect();
    setCaretAnchor({ getBoundingClientRect: () => caretRect });
  }, []);

  // Escape typed in the editable closes the full toolbar, as it does from inside it.
  useEffect(() => {
    if (!open) return;
    const editable = toolbarRef.current?.closest<HTMLElement>('[contenteditable="true"]');
    if (!editable) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!getIsEscapeKey(event)) return;
      event.preventDefault();
      event.stopPropagation();
      close();
    };
    editable.addEventListener('keydown', onKeyDown, true);
    return () => editable.removeEventListener('keydown', onKeyDown, true);
  }, [close, open]);

  return (
    <span
      ref={toolbarRef}
      aria-label="Insert into empty paragraph"
      role="toolbar"
      style={{ display: 'inline-flex', gap: 2 }}
      onMouseDown={event => event.preventDefault()}
    >
      {!open && (
        <>
          {EMPTY_PARAGRAPH_ITEMS.map(item => (
            <ToolbarItemRenderer key={item.id} item={item} />
          ))}
          {/* A span, not the default <hr>: this toolbar renders inside the paragraph's <p>. */}
          <Divider flexItem component="span" orientation="vertical" />
          <AlineTooltip arrow placement="top" title="More formatting options">
            <IconButton
              aria-expanded={false}
              aria-label="More formatting options"
              className={clsx(classes.toolbarButton, classes.toolbarButtonDefault)}
              size="small"
              onClick={openAtCaret}
            >
              <ChevronRight size={ICON_SIZE} />
            </IconButton>
          </AlineTooltip>
        </>
      )}
      <Popper
        anchorEl={caretAnchor}
        className="ignore-click-outside/toolbar"
        open={open}
        placement="top-start"
        sx={{ zIndex: theme => theme.zIndex.tooltip }}
      >
        <ClickAwayListener onClickAway={close}>
          <Box
            aria-label="More text formatting"
            className={classes.formatToolbar}
            role="toolbar"
            sx={{ mb: 1 }}
            onKeyDown={event => {
              if (!getIsEscapeKey(event.nativeEvent)) return;
              event.preventDefault();
              event.stopPropagation();
              close();
            }}
          >
            <AlineMarkdownEditorToolbar />
          </Box>
        </ClickAwayListener>
      </Popper>
    </span>
  );
});

function getIsEscapeKey(event: KeyboardEvent) {
  return event.key === 'Escape' && !event.isComposing && event.keyCode !== IME_COMPOSING_KEY_CODE;
}
